import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import PanelLinks from "@/components/feature/panel/PanelLinks";
import ChildSelector from "@/pages/veli/components/ChildSelector";
import { useVeliChild, useChildWeeklyReport } from "@/hooks/useVeli";

export default function HaftalikRapor() {
  const { children, activeChild, activeUserId, setActiveUserId, loading: childrenLoading } =
    useVeliChild();
  const { data, loading, error, reload } = useChildWeeklyReport(activeChild?.userId ?? null);

  if (childrenLoading) return <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />;

  if (children.length === 0) {
    return (
      <div className="space-y-6">
        <SectionHeader
          title="Haftalık Rapor"
          description="Öğrencinin haftalık gelişim özetini takip et."
        />
        <EmptyState
          icon="ri-bar-chart-line"
          title="Henüz öğrenci hesabı bağlanmadı"
          description="Çocuğunuzun hesabını eşleştirdiğinizde haftalık raporu burada görünecek."
        />
        <PanelLinks
          links={[{ label: "Çocuğunu Eşleştir", href: "/veli/profil", icon: "ri-link" }]}
        />
      </div>
    );
  }

  const report = data;
  const maxMinutes = report?.studyDays?.length
    ? Math.max(1, ...report.studyDays.map((d) => d.minutes))
    : 1;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Haftalık Rapor"
        description={`${activeChild?.fullName ?? "Öğrencinin"} haftalık çalışma ve gelişim özeti.`}
      />
      <ChildSelector
        children={children}
        activeUserId={activeUserId}
        onSelect={setActiveUserId}
      />

      {loading ? (
        <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />
      ) : error ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            Tekrar dene
          </button>
        </div>
      ) : !report ? (
        <EmptyState
          icon="ri-bar-chart-line"
          title="Henüz rapor verisi yok"
          description="İlk haftalık rapor, veri toplandığında burada görünecek."
        />
      ) : (
        <>
          {/* Özet kartlar */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <p className="text-xs font-medium text-foreground-500">Bu hafta çalışma süresi</p>
              <p className="mt-2 font-heading font-bold text-2xl text-foreground-950">
                {report.weekMinutes} dk
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <p className="text-xs font-medium text-foreground-500">Bugünkü görevler</p>
              <p className="mt-2 font-heading font-bold text-2xl text-foreground-950">
                {report.completedTasks} / {report.totalTasks}
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <p className="text-xs font-medium text-foreground-500">Son deneme neti</p>
              <p className="mt-2 font-heading font-bold text-2xl text-accent-700">
                {report.lastExam ? report.lastExam.net : "—"}
              </p>
              {report.lastExam && (
                <p className="mt-1 text-xs text-foreground-500">{report.lastExam.name}</p>
              )}
            </div>
          </div>

          {/* Haftalık çalışma grafiği */}
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading font-semibold text-base text-foreground-950">
              Günlük Çalışma
            </h2>
            <div className="mt-5 flex items-end gap-2 h-40">
              {report.studyDays.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-2">
                  <span className="text-xs text-foreground-500 font-medium">
                    {d.minutes > 0 ? `${d.minutes}` : ""}
                  </span>
                  <div
                    className="w-full max-w-[36px] rounded-t-md bg-primary-500 transition-all"
                    style={{ height: `${Math.max(d.minutes > 0 ? 8 : 3, (d.minutes / maxMinutes) * 100)}%` }}
                  />
                  <span className="text-xs text-foreground-500">{d.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Eksik konular */}
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading font-semibold text-base text-foreground-950">
              Eksik Konular
            </h2>
            {report.weakTopics.length === 0 ? (
              <p className="mt-3 text-sm text-foreground-500">Henüz eksik konu tespit edilmedi.</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {report.weakTopics.map((w) => (
                  <li
                    key={w.id}
                    className="flex items-center gap-3 rounded-md border border-background-200 bg-background-100/50 px-4 py-3"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 shrink-0">
                      <i className="ri-alert-line" aria-hidden="true"></i>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground-900">
                        {w.subject} — {w.topic}
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-foreground-700 whitespace-nowrap">
                      %{w.mastery_percent}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      <PanelLinks
        links={[
          { label: "Deneme Sonuçları", href: "/veli/deneme-sonuclari", icon: "ri-file-text-line" },
          { label: "AI Öğrenci Analizi", href: "/veli/ai-analiz", icon: "ri-brain-line" },
        ]}
      />
    </div>
  );
}